<template>
  <div class="modal fade show d-block" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl" role="document"> <!-- Modal ist breiter -->
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ initialDraft ? 'Rechnung duplizieren' : isNew ? $t('modal.editModal.newTitle'): $t('modal.editModal.editTitle')}}</h5>
          <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <p v-if="initialDraft" class="alert alert-info">Die Kopie ist ein neuer Entwurf ohne Quelldatei oder Zahlungszuordnungen. Bitte eine neue Rechnungsnummer vergeben und Datum, Leistungszeitraum sowie Zahlungsbedingungen prüfen.</p>
          <p v-if="copyError" class="alert alert-danger" role="alert">{{ copyError }}</p>
          <!-- Toggle zwischen Form und JSON-Editor -->
          <button
              class="btn btn-primary mb-3"
              @click="toggleEditMode">
            {{ isJsonMode ? 'Switch to Form View' : 'Switch to JSON View' }}
          </button>
          <div v-if="isNew && !initialDraft" class="mb-3">
            <div class="btn-group" role="group" aria-label="Invoice creation mode">
              <input
                  id="invoiceModeConvert"
                  class="btn-check"
                  type="radio"
                  name="invoiceMode"
                  value="convert"
                  v-model="invoiceCreationMode"
                  autocomplete="off"
              />
              <label class="btn btn-outline-primary btn-sm" for="invoiceModeConvert" data-testid="invoice-mode-convert">
                {{ $t("modal.editModal.modeConvert") }}
              </label>

              <input
                  id="invoiceModeCreate"
                  class="btn-check"
                  type="radio"
                  name="invoiceMode"
                  value="create"
                  v-model="invoiceCreationMode"
                  autocomplete="off"
              />
              <label class="btn btn-outline-primary btn-sm" for="invoiceModeCreate" data-testid="invoice-mode-create">
                {{ $t("modal.editModal.modeCreate") }}
              </label>
            </div>
          </div>

          <div v-if="isNew && invoiceCreationMode === 'convert'" class="d-flex flex-row justify-content-between align-items-end gap-3 mb-4" data-testid="invoice-convert-controls">
            <!-- Datei-Upload (nur für Rechnungskonvertierung) -->
            <div class="form-group" style="width: 55%">
              <label for="fileUpload" class="form-label">Upload File</label>
              <input
                  type="file"
                  id="fileUpload"
                  @change="uploadFile"
                  class="form-control"
                  data-testid="invoice-sourcefile-upload"
              />
            </div>
            <div class="form-group" style="width: 15%">
              <SearchableDropdown input-id="parser" label="Parser" :model-value="selectedParser"
                                  :options="masterDataStore.invoiceParserTypes"
                                  @update:modelValue="val => { selectedParser = val; }"
              />
            </div>
            <div class="form-group" style="width: 15%">
              <SearchableDropdown input-id="ocrEngine" label="OCR Engine" :model-value="selectedOcrEngineType"
                                  :options="ocrEngineTypes"
                                  @update:modelValue="val => { selectedOcrEngineType = val; }"
              />
            </div>
            <button :disabled="!this.zugferdData.sourceFileId || isLoading" class="btn custom-btn"
                    style="width: 15%; height: 38px"
                    data-testid="invoice-parse-button"
                    @click="setInvoiceData"
            >
              Parse
            </button>
          </div>
          <!-- ZUGFeRD Form oder JSON-Editor anzeigen -->
          <div v-if="!isJsonMode">
            <ZUGFeRDForm
                :zugferd="zugferdData"
                :is-manual-create-mode="isNew && !initialDraft && invoiceCreationMode === 'create'"
                @formSubmitted="handleFormSubmit"
                @update-zugferd="updateZugferdData"
            />
          </div>

          <div v-else>
            <label for="jsonInput" class="form-label">JSON Input</label>
            <textarea
                id="jsonInput"
                v-model="jsonInput"
                class="form-control"
                rows="14"
                @input="formatJson"
            ></textarea>
            <p class="text-danger" v-if="validationError">{{ validationError }}</p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="handleCancel">{{ $t('modal.editModal.cancel') }}</button>
          <button
               type="button"
               class="btn btn-primary"
               data-testid="invoice-save-button"
               :disabled="isConvertModeWithoutSourceFile || saving"
               @click="submitForm('save')"
           >
             {{ saveButtonLabel }}
           </button>
          <button
               type="button"
               class="btn btn-success"
               data-testid="invoice-save-validate-button"
               :disabled="isConvertModeWithoutSourceFile || saving"
               @click="submitForm('saveAndValidate')"
           >
             {{ saveAndValidateButtonLabel }}
           </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ZUGFeRDForm from './ZUGFeRDForm.vue';
import ZUGFeRD from '../../models/ZUGFeRD';
import { hasNewInvoiceNumber } from "@/utils/invoiceCopy.cjs";
import InvoiceService from '@/services/InvoiceService';
//import axios from 'axios';
import apiClient from '@/axiosConfig';
import {useUxStore} from "@/stores/uxStore";
import SearchableDropdown from "@/components/Shared/SearchableDropdown.vue";
import {useMasterDataStore} from "@/stores/masterDataStore";
import { useUserStore } from "@/stores/userStore";

export default {
  name: 'InvoiceModal',
  components: {SearchableDropdown, ZUGFeRDForm},
  props: {
    initialDraft: { type: Object, default: null },
    copySourceNumber: { type: String, default: "" },
    saving: { type: Boolean, default: false },
    zugferd: {
      type: Object,
      required: false,
      default: () => ({})
    },
    isNew: {
      type: Boolean,
      default: false
    }
  },
  data() {
       return {
         submitAction: null,
         copyError: "",
         zugferdData: this.initialDraft ? new ZUGFeRD(this.initialDraft) : this.isNew ? new ZUGFeRD() : {...this.zugferd},
         initialZugferdSnapshot: this.initialDraft ? new ZUGFeRD(this.initialDraft) : this.isNew ? new ZUGFeRD() : JSON.parse(JSON.stringify(this.zugferd)), // Snapshot of initial data
       invoiceCreationMode: "create",
         isJsonMode: false,
         jsonInput: '',
         validationError: null,
      selectedParser: '500',
      selectedOcrEngineType: '100',
      ocrEngineTypes: [
        { id: '100', name: 'Auto' },
        { id: '200', name: 'Tesseract' },
        { id: '300', name: 'Azure Image Analysis (Legacy)' },
        { id: '400', name: 'Azure Computer Vision Read (New)' }
      ],
      masterDataStore: useMasterDataStore(),
      userStore: useUserStore()
    };
  },
  computed: {
    isLoading() {
      const uxStore = useUxStore();
      return uxStore.isLoading;
    },
    isDraftInvoice() {
      return !this.zugferdData?.fileId;
    },
    saveButtonLabel() {
      return this.isDraftInvoice ? this.$t("modal.editModal.saveDraft") : this.$t("modal.editModal.save");
    },
    saveAndValidateButtonLabel() {
      return this.isDraftInvoice
        ? this.$t("modal.editModal.finalizeAndValidate")
        : this.$t("modal.editModal.saveAndValidate");
    },
     hasChanges() {
       // Compare current data with the initial snapshot
       return JSON.stringify(this.zugferdData) !== JSON.stringify(this.initialZugferdSnapshot);
     },
     isConvertModeWithoutSourceFile() {
       return this.isNew && this.invoiceCreationMode === "convert" && !this.zugferdData?.sourceFileId;
     }
   },
  methods: {
    async toggleEditMode() {
      if (!this.isJsonMode) {
        this.jsonInput = JSON.stringify(this.zugferdData, null, 2);
      } else {
        await this.validateAndProcessJson();
      }
      this.isJsonMode = !this.isJsonMode;
    },
    async setInvoiceData() {
      try {
        if (this.zugferdData.sourceFileId) {
          console.log("Fetching invoice data from file:", this.zugferdData.sourceFileId);
          const invoice = await InvoiceService.getInvoiceFromFile(
              this.zugferdData.sourceFileId,
              this.selectedParser,
              this.zugferdData.invoice.invoiceTypeId,
              this.selectedOcrEngineType
          );
          console.log("Fetched invoice data:", invoice);
          if (!invoice) {
            return;
          }
          this.zugferdData.invoice = {...invoice};
          console.log("Updated zugferdData.invoice:", this.zugferdData.invoice);
        }
      } finally {
        await this.userStore.loadMe().catch(() => undefined);
      }
    },
    async uploadFile(event) {
      const uXStore = useUxStore();
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        uXStore.startLoading();
        const response = await apiClient.post("/file/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.zugferdData.sourceFileId = response.data.id;
      } catch (error) {
        console.error("File upload failed:", error);
      } finally {
        uXStore.stopLoading();
      }
    },
    formatJson() {
      try {
        this.jsonInput = JSON.stringify(JSON.parse(this.jsonInput), null, 2);
        this.validationError = null;
      } catch (error) {
        this.validationError = "Invalid JSON format.";
      }
    },
    async validateAndProcessJson() {
      try {
        const parsedData = JSON.parse(this.jsonInput);
        const validatedData = new ZUGFeRD(parsedData);
        Object.assign(this.zugferdData, validatedData);
        this.validationError = null;
      } catch (error) {
        this.validationError = "Invalid JSON format or schema.";
      }
    },
    handleFormSubmit(formData, action = 'save') {
      if (this.saving) return;
      if (this.initialDraft && !hasNewInvoiceNumber(formData?.invoice?.invoiceNumber, this.copySourceNumber)) {
        this.copyError = "Bitte eine neue Rechnungsnummer für die Kopie eingeben.";
        return;
      }
      this.copyError = "";
      // Use the stored action if it exists, otherwise use the provided action
      const finalAction = this.submitAction || action;
      this.submitAction = null; // Clear it after use

      this.$emit('formSubmitted', formData, finalAction);
    },
    submitForm(action) {
      // Store the action in a data property so ZUGFeRDForm can access it
      this.submitAction = action;

      // Directly trigger the form submission
      const form = document.getElementById('zugferdform');
      if (form) {
        // Create and dispatch a custom submit event
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(submitEvent);
      }
    },
    updateZugferdData(updatedZugferd) {
      Object.assign(this.zugferdData, updatedZugferd);
      console.log("InvoiceModal updated zugferdData:", this.zugferdData);
    },
    handleCancel() {
      if (this.hasChanges) {
        const confirmDiscard = confirm("Are you sure you want to discard your changes?");
        if (confirmDiscard) {
          this.$emit('close');
        }
      } else {
        this.$emit('close');
      }
    }
  },
  watch: {
    invoiceCreationMode: {
      handler(newMode) {
        if (this.isNew && newMode === "create") {
          // Ensure we don't accidentally submit a conversion request when creating a fresh invoice.
          this.zugferdData.sourceFileId = null;
        }
      }
    },
    zugferd: {
      immediate: true,
      handler(newVal) {
        if (!this.isNew) {
          this.zugferdData = {...newVal};
          this.initialZugferdSnapshot = JSON.parse(JSON.stringify(newVal)); // Update initial snapshot when prop changes
          console.log("Modal data on prop update:", this.zugferdData);
        }
      }
    }
  },
};
</script>


<style scoped>
.modal.fade.show {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}
</style>

